import type {AutoDeliveryDcxUpdateReasonStatus} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import {
    AUTO_DELIVERY_DCX_ORDER_STATUS_TYPES,
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES,
} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import appDateTimeService from '@/services/appDateTimeService';
import appContent from '@/services/content/appContent.typed';
import type {InlineNotificationModel} from '@/types/aem';
import {
    AUTO_DELIVERY_INFO_BOX_SOURCES,
    type AutoDeliveryInfoBoxData,
    type AutoDeliveryInfoBoxSource,
} from '@/types/content';
import type {AutoDeliveryDetailsDcx} from '@/types/icc/autoDeliveryDetails';
import stringUtils from '@/utils/stringUtils';

const ACTIVE_CONTENT_VARIANTS = {
    OUT_OF_STOCK_ON_HOLD: 'outOfStockOnHold',
    OUT_OF_STOCK_PARTIAL_DELIVERY: 'outOfStockPartialDelivery',
    SKIPPED_DELIVERY: 'skippedDelivery',
    EXPECTED_DELIVERY: 'expectedDelivery',
    ACTIVE_DELIVERY: 'activeDelivery',
} as const;

type ActiveContentVariant = (typeof ACTIVE_CONTENT_VARIANTS)[keyof typeof ACTIVE_CONTENT_VARIANTS];

export const isActiveOutOfStockOnHold = (statusUpdateReason: AutoDeliveryDcxUpdateReasonStatus | null): boolean => {
    // TODO: add third Active Out Of Stock status once known
    return (
        statusUpdateReason === AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_LSP_01 ||
        statusUpdateReason === AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_LSP_02
    );
};

interface GetActiveContentVariantParams {
    statusUpdateReason: AutoDeliveryDcxUpdateReasonStatus | null;
    isExpectedOrder: boolean;
    isDeliverySkipped?: boolean;
}

const getActiveContentVariant = (params: GetActiveContentVariantParams): ActiveContentVariant | null => {
    const {statusUpdateReason, isExpectedOrder, isDeliverySkipped} = params;

    if (isExpectedOrder) {
        switch (statusUpdateReason) {
            case AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_STOCK_01:
                // TODO: add second ActiveExpected Out Of Stock status once known
                return ACTIVE_CONTENT_VARIANTS.OUT_OF_STOCK_ON_HOLD;
            default:
                if (isDeliverySkipped) return ACTIVE_CONTENT_VARIANTS.SKIPPED_DELIVERY;
                return ACTIVE_CONTENT_VARIANTS.EXPECTED_DELIVERY;
        }
    }

    if (isActiveOutOfStockOnHold(statusUpdateReason)) {
        return ACTIVE_CONTENT_VARIANTS.OUT_OF_STOCK_ON_HOLD;
    }

    // TODO: replace ERR_AD_OOS_PARTIAL_01 with real flag once known
    if (statusUpdateReason === AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_OOS_PARTIAL_01) {
        return ACTIVE_CONTENT_VARIANTS.OUT_OF_STOCK_PARTIAL_DELIVERY;
    }

    return ACTIVE_CONTENT_VARIANTS.ACTIVE_DELIVERY;
};

const formatNotificationDescription = (
    notification: InlineNotificationModel | undefined,
    args: string[]
): InlineNotificationModel | undefined => {
    if (!notification?.description || !args.length) return notification;

    return {...notification, description: stringUtils.formatString(notification.description, ...args)};
};

interface GetActiveInfoBoxDataParams {
    details: AutoDeliveryDetailsDcx | undefined;
    source: AutoDeliveryInfoBoxSource;
    isDeliverySkipped?: boolean;
}

export const getActiveInfoBoxData = (params: GetActiveInfoBoxDataParams): AutoDeliveryInfoBoxData | undefined => {
    const {details, source, isDeliverySkipped} = params;
    const statusUpdateReason = details?.autoDelivery?.statusUpdateReason ?? null;
    const orderStatus = details?.order?.status;
    const isExpectedOrder = orderStatus === AUTO_DELIVERY_DCX_ORDER_STATUS_TYPES.EXPECTED;
    const isFreezedOrder = details?.autoDelivery?.isFreezed;
    const hasFreezeDate = !!details?.autoDelivery?.freezeDate;

    const variant = getActiveContentVariant({statusUpdateReason, isExpectedOrder, isDeliverySkipped});

    if (!variant) return undefined;

    const content = appContent.getAutoDeliveryContent();
    const isDashboard = source === AUTO_DELIVERY_INFO_BOX_SOURCES.DASHBOARD;

    const getOutOfStockArgs = (): string[] => {
        const nextDeliveryDate = details?.autoDelivery?.nextDeliveryDate;
        const deliveryMonth = nextDeliveryDate ? appDateTimeService.getLocalizedMonth(nextDeliveryDate) : undefined;

        return deliveryMonth ? [deliveryMonth] : [];
    };

    const getExpectedDeliveryArgs = (): string[] => {
        const freezeDate = details?.autoDelivery?.freezeDate;
        const freezeDateFormatted = freezeDate ? appDateTimeService.getLocalizedDayMonthAndYear(freezeDate) : undefined;

        return freezeDateFormatted ? [freezeDateFormatted] : [];
    };

    const buildResult = (notification: InlineNotificationModel | undefined): AutoDeliveryInfoBoxData | undefined => {
        if (!notification) return undefined;

        return {notification};
    };

    switch (variant) {
        case ACTIVE_CONTENT_VARIANTS.OUT_OF_STOCK_ON_HOLD:
            return buildResult(
                formatNotificationDescription(
                    isExpectedOrder
                        ? isDashboard
                            ? content?.dashboardContent?.activeExpectedOrderOutOfStockOnHoldDashboard
                                  ?.inlineNotification
                            : content?.extendedDetailsContent?.activeExpectedOrderOutOfStockOnHold
                                  ?.orderDetailsInlineNotification
                        : isDashboard
                          ? content?.dashboardContent?.activeOrderOutOfStockOnHoldDashboard?.inlineNotification
                          : content?.extendedDetailsContent?.activeOrderOutOfStockOnHold
                                ?.orderDetailsInlineNotification,
                    getOutOfStockArgs()
                )
            );
        case ACTIVE_CONTENT_VARIANTS.OUT_OF_STOCK_PARTIAL_DELIVERY:
            return buildResult(
                formatNotificationDescription(
                    isDashboard
                        ? content?.dashboardContent?.activeOrderOutOfStockPartialDeliveryDashboard?.inlineNotification
                        : content?.extendedDetailsContent?.activeOrderOutOfStockPartialDelivery
                              ?.orderDetailsInlineNotification,
                    getOutOfStockArgs()
                )
            );
        case ACTIVE_CONTENT_VARIANTS.SKIPPED_DELIVERY:
            return buildResult(content?.skipDeliveryContent?.nextAutoDeliverySkippedInlineNotification);
        case ACTIVE_CONTENT_VARIANTS.EXPECTED_DELIVERY:
            return isDashboard || !hasFreezeDate
                ? buildResult(undefined)
                : buildResult(
                      formatNotificationDescription(
                          content?.extendedDetailsContent?.activeOrderExpected?.orderDetailsInlineNotification,
                          getExpectedDeliveryArgs()
                      )
                  );
        case ACTIVE_CONTENT_VARIANTS.ACTIVE_DELIVERY:
            return isDashboard || !isFreezedOrder
                ? buildResult(undefined)
                : buildResult(content?.extendedDetailsContent?.activeOrder?.orderDetailsInlineNotification);
        default:
            return undefined;
    }
};
